/**
 * Sales Channel Management - Admin Portal
 * Multi-Channel Sales Management for external connections and fee mapping
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Smartphone, Globe, ShoppingBag, MessageCircle, Store, Settings,
  Plus, Edit2, Trash2, Key, Webhook, Rss, RefreshCw, Copy, Eye, EyeOff,
  Check, X, AlertCircle, DollarSign, Percent, ChevronDown, ChevronRight,
  ExternalLink, Activity, TrendingUp, Zap
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Channel type icons and labels
const CHANNEL_TYPES = {
  mobile_app: { icon: Smartphone, label: 'Mobile App', color: 'bg-blue-100 text-blue-800' },
  custom_webstore: { icon: Globe, label: 'Custom Webstore', color: 'bg-purple-100 text-purple-800' },
  social_commerce: { icon: Rss, label: 'Social Commerce', color: 'bg-pink-100 text-pink-800' },
  whatsapp: { icon: MessageCircle, label: 'WhatsApp', color: 'bg-green-100 text-green-800' },
  pos: { icon: Store, label: 'POS', color: 'bg-orange-100 text-orange-800' },
  shopify: { icon: ShoppingBag, label: 'Shopify', color: 'bg-emerald-100 text-emerald-800' },
  woocommerce: { icon: ShoppingBag, label: 'WooCommerce', color: 'bg-indigo-100 text-indigo-800' }
};

const PAYMENT_PROVIDERS = ['razorpay', 'stripe', 'paypal', 'unlimit', 'paytm', 'phonepe', 'cod'];
const PAYMENT_METHODS = ['upi', 'credit_card', 'debit_card', 'net_banking', 'wallet', 'emi', 'bnpl', 'cod', 'international_card'];
const MARKETS = [
  { code: '*', label: 'All Markets' },
  { code: 'IN', label: 'India' },
  { code: 'US', label: 'United States' },
  { code: 'UK', label: 'United Kingdom' },
  { code: 'UAE', label: 'UAE' },
  { code: 'SG', label: 'Singapore' }
];

// ============================================================================
// Channel Card Component
// ============================================================================
const ChannelCard = ({ channel, onEdit, onDelete, onManageKeys, onManageWebhooks, onManageFeeds }) => {
  const channelType = CHANNEL_TYPES[channel.channel_type] || CHANNEL_TYPES.custom_webstore;
  const Icon = channelType.icon;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${channelType.color}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold">{channel.name}</h3>
              <p className="text-sm text-gray-500">{channel.description || channelType.label}</p>
            </div>
          </div>
          <Badge variant={channel.status === 'active' ? 'default' : 'secondary'}>
            {channel.status}
          </Badge>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Collection Rate:</span>
            <span className="ml-2 font-medium">{channel.channel_collection_rate || channel.channel_fee_rate || 0}%</span>
          </div>
          <div>
            <span className="text-gray-500">Type:</span>
            <span className="ml-2">{channelType.label}</span>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {channel.channel_type === 'mobile_app' && (
            <Button variant="outline" size="sm" onClick={() => onManageKeys(channel)}>
              <Key className="h-4 w-4 mr-1" /> API Keys
            </Button>
          )}
          {channel.channel_type === 'custom_webstore' && (
            <Button variant="outline" size="sm" onClick={() => onManageWebhooks(channel)}>
              <Webhook className="h-4 w-4 mr-1" /> Webhooks
            </Button>
          )}
          {channel.channel_type === 'social_commerce' && (
            <Button variant="outline" size="sm" onClick={() => onManageFeeds(channel)}>
              <Rss className="h-4 w-4 mr-1" /> Feeds
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onEdit(channel)}>
            <Edit2 className="h-4 w-4 mr-1" /> Edit
          </Button>
          <Button variant="outline" size="sm" className="text-red-600" onClick={() => onDelete(channel)}>
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// Gateway Fee Rule Row Component
// ============================================================================
const GatewayFeeRuleRow = ({ rule, onEdit, onDelete }) => {
  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="font-medium">{rule.name}</div>
        <div className="text-xs text-gray-500">{rule.description}</div>
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline">{rule.market === '*' ? 'All' : rule.market}</Badge>
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline">{rule.channel_type === '*' ? 'All' : rule.channel_type}</Badge>
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline">{rule.payment_provider === '*' ? 'All' : rule.payment_provider}</Badge>
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline">{rule.payment_method === '*' ? 'All' : rule.payment_method}</Badge>
      </td>
      <td className="px-4 py-3 text-right">
        {rule.transaction_fee_rate}% + ₹{rule.transaction_fee_fixed}
      </td>
      <td className="px-4 py-3 text-right">
        {rule.gst_on_fee_rate}%
      </td>
      <td className="px-4 py-3 text-center">
        <Badge variant={rule.is_active ? 'default' : 'secondary'}>
          {rule.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(rule)}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onDelete(rule)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

// ============================================================================
// Add/Edit Channel Modal
// ============================================================================
const ChannelModal = ({ isOpen, onClose, onSave, channel, mode }) => {
  const getInitialFormData = () => {
    if (channel && mode === 'edit') {
      return {
        name: channel.name,
        channel_type: channel.channel_type,
        description: channel.description || '',
        channel_collection_rate: channel.channel_collection_rate || channel.channel_fee_rate || 0,
        config: channel.config || {}
      };
    }
    return {
      name: '',
      channel_type: 'mobile_app',
      description: '',
      channel_collection_rate: 0,
      config: {}
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);

  // Reset form when modal opens/closes or channel changes
  const resetKey = `${isOpen}-${channel?.id}-${mode}`;
  useEffect(() => {
    setFormData(getInitialFormData());
  }, [resetKey]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {mode === 'edit' ? 'Edit Sales Channel' : 'Add Sales Channel'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Channel Name</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Mobile App, Custom Store"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Channel Type</label>
            <select
              className="w-full border rounded-md p-2"
              value={formData.channel_type}
              onChange={(e) => setFormData({ ...formData, channel_type: e.target.value })}
            >
              {Object.entries(CHANNEL_TYPES).map(([key, value]) => (
                <option key={key} value={key}>{value.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this channel"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Channel Collection Rate (%)</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.channel_collection_rate}
              onChange={(e) => setFormData({ ...formData, channel_collection_rate: parseFloat(e.target.value) || 0 })}
            />
            <p className="text-xs text-gray-500 mt-1">
              <strong>Deferred Collection:</strong> This is the fee the Marketplace will be invoiced for by the channel 
              (e.g., Shopify, Meta). This amount is collected from the Vendor during payout to cover the future expense.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">{mode === 'edit' ? 'Update' : 'Create'} Channel</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// Add/Edit Gateway Fee Rule Modal
// ============================================================================
const GatewayFeeRuleModal = ({ isOpen, onClose, onSave, rule, mode }) => {
  const getInitialFormData = () => {
    if (rule && mode === 'edit') {
      return {
        name: rule.name,
        description: rule.description || '',
        market: rule.market,
        channel_type: rule.channel_type,
        payment_provider: rule.payment_provider,
        payment_method: rule.payment_method,
        transaction_fee_rate: rule.transaction_fee_rate,
        transaction_fee_fixed: rule.transaction_fee_fixed,
        gst_on_fee_rate: rule.gst_on_fee_rate,
        currency_conversion_fee: rule.currency_conversion_fee || 0,
        priority: rule.priority
      };
    }
    return {
      name: '',
      description: '',
      market: '*',
      channel_type: '*',
      payment_provider: '*',
      payment_method: '*',
      transaction_fee_rate: 0,
      transaction_fee_fixed: 0,
      gst_on_fee_rate: 18,
      currency_conversion_fee: 0,
      priority: 0
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);

  // Reset form when modal opens/closes or rule changes
  const resetKey = `${isOpen}-${rule?.id}-${mode}`;
  useEffect(() => {
    setFormData(getInitialFormData());
  }, [resetKey]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl m-4">
        <h2 className="text-xl font-bold mb-4">
          {mode === 'edit' ? 'Edit Gateway Fee Rule' : 'Add Gateway Fee Rule'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Rule Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Razorpay UPI - India"
                required
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this rule"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Market/Country</label>
              <select
                className="w-full border rounded-md p-2"
                value={formData.market}
                onChange={(e) => setFormData({ ...formData, market: e.target.value })}
              >
                {MARKETS.map((m) => (
                  <option key={m.code} value={m.code}>{m.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Channel Type</label>
              <select
                className="w-full border rounded-md p-2"
                value={formData.channel_type}
                onChange={(e) => setFormData({ ...formData, channel_type: e.target.value })}
              >
                <option value="*">All Channels</option>
                {Object.entries(CHANNEL_TYPES).map(([key, value]) => (
                  <option key={key} value={key}>{value.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Payment Provider</label>
              <select
                className="w-full border rounded-md p-2"
                value={formData.payment_provider}
                onChange={(e) => setFormData({ ...formData, payment_provider: e.target.value })}
              >
                <option value="*">All Providers</option>
                {PAYMENT_PROVIDERS.map((p) => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Payment Method</label>
              <select
                className="w-full border rounded-md p-2"
                value={formData.payment_method}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
              >
                <option value="*">All Methods</option>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>{m.replace('_', ' ').toUpperCase()}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Transaction Fee Rate (%)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.transaction_fee_rate}
                onChange={(e) => setFormData({ ...formData, transaction_fee_rate: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Fixed Fee (₹)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.transaction_fee_fixed}
                onChange={(e) => setFormData({ ...formData, transaction_fee_fixed: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">GST on Fee (%)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.gst_on_fee_rate}
                onChange={(e) => setFormData({ ...formData, gst_on_fee_rate: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Currency Conversion Fee (%)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.currency_conversion_fee}
                onChange={(e) => setFormData({ ...formData, currency_conversion_fee: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Priority</label>
              <Input
                type="number"
                min="0"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
              />
              <p className="text-xs text-gray-500 mt-1">Higher priority rules are matched first</p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">{mode === 'edit' ? 'Update' : 'Create'} Rule</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// API Key Management Modal
// ============================================================================
const APIKeyModal = ({ isOpen, onClose, channel, token }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const { toast } = useToast();

  const fetchApiKeys = useCallback(async () => {
    if (!channel) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/sales-channels/${channel.id}/api-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  }, [channel, token]);

  useEffect(() => {
    if (isOpen && channel) {
      fetchApiKeys();
      setGeneratedKey(null);
    }
  }, [isOpen, channel, fetchApiKeys]);

  const generateKey = async () => {
    if (!newKeyName.trim()) {
      toast({ title: 'Error', description: 'Please enter a key name', variant: 'destructive' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/sales-channels/${channel.id}/api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          channel_id: channel.id,
          name: newKeyName,
          permissions: ['read_products', 'create_orders'],
          rate_limit_per_minute: 60,
          rate_limit_per_day: 10000
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedKey(data.api_key);
        setNewKeyName('');
        fetchApiKeys();
        toast({ title: 'Success', description: 'API key generated successfully' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to generate API key', variant: 'destructive' });
    }
  };

  const revokeKey = async (keyId) => {
    try {
      const response = await fetch(`${API_URL}/api/sales-channels/${channel.id}/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchApiKeys();
        toast({ title: 'Success', description: 'API key revoked' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to revoke API key', variant: 'destructive' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied', description: 'API key copied to clipboard' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">API Key Management - {channel?.name}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Generate New Key */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <h3 className="font-medium mb-3">Generate New API Key</h3>
            <div className="flex gap-2">
              <Input
                placeholder="Key name (e.g., iOS App Production)"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
              <Button onClick={generateKey}>
                <Key className="h-4 w-4 mr-1" /> Generate
              </Button>
            </div>

            {generatedKey && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center gap-2 text-yellow-800 mb-2">
                  <AlertCircle className="h-4 w-4" />
                  <span className="font-medium">Save this key now! It will not be shown again.</span>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white p-2 rounded border font-mono text-sm">
                    {showKey ? generatedKey : '••••••••••••••••••••••••••••••••'}
                  </code>
                  <Button variant="outline" size="sm" onClick={() => setShowKey(!showKey)}>
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(generatedKey)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Existing Keys */}
        <h3 className="font-medium mb-3">Active API Keys</h3>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No API keys configured
          </div>
        ) : (
          <div className="space-y-2">
            {apiKeys.map((key) => (
              <div key={key.id} className="flex items-center justify-between p-3 border rounded-md">
                <div>
                  <div className="font-medium">{key.name}</div>
                  <div className="text-sm text-gray-500">
                    Prefix: {key.key_prefix}... | Requests: {key.total_requests}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={key.is_active ? 'default' : 'secondary'}>
                    {key.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600"
                    onClick={() => revokeKey(key.id)}
                  >
                    Revoke
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================
const SalesChannelManagement = () => {
  const [channels, setChannels] = useState([]);
  const [gatewayRules, setGatewayRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('channels');
  
  // Modal states
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [showGatewayModal, setShowGatewayModal] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [modalMode, setModalMode] = useState('add');

  const { token } = useAuth();
  const { toast } = useToast();

  // Fetch channels
  const fetchChannels = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/sales-channels`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChannels(data);
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  }, [token]);

  // Fetch gateway rules
  const fetchGatewayRules = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/sales-channels/fees/gateway-rules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGatewayRules(data);
      }
    } catch (error) {
      console.error('Error fetching gateway rules:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchChannels(), fetchGatewayRules()]);
      setLoading(false);
    };
    loadData();
  }, [fetchChannels, fetchGatewayRules]);

  // Channel CRUD
  const handleSaveChannel = async (data) => {
    try {
      const url = modalMode === 'edit'
        ? `${API_URL}/api/sales-channels/${selectedChannel.id}`
        : `${API_URL}/api/sales-channels`;
      
      const response = await fetch(url, {
        method: modalMode === 'edit' ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        toast({ title: 'Success', description: `Channel ${modalMode === 'edit' ? 'updated' : 'created'} successfully` });
        setShowChannelModal(false);
        fetchChannels();
      } else {
        throw new Error('Failed to save channel');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleDeleteChannel = async (channel) => {
    if (!window.confirm(`Are you sure you want to delete "${channel.name}"?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/sales-channels/${channel.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Channel deleted successfully' });
        fetchChannels();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete channel', variant: 'destructive' });
    }
  };

  // Gateway Rule CRUD
  const handleSaveGatewayRule = async (data) => {
    try {
      const url = modalMode === 'edit'
        ? `${API_URL}/api/sales-channels/fees/gateway-rules/${selectedRule.id}`
        : `${API_URL}/api/sales-channels/fees/gateway-rules`;
      
      const response = await fetch(url, {
        method: modalMode === 'edit' ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        toast({ title: 'Success', description: `Gateway rule ${modalMode === 'edit' ? 'updated' : 'created'} successfully` });
        setShowGatewayModal(false);
        fetchGatewayRules();
      } else {
        throw new Error('Failed to save gateway rule');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleDeleteGatewayRule = async (rule) => {
    if (!window.confirm(`Are you sure you want to delete "${rule.name}"?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/sales-channels/fees/gateway-rules/${rule.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Gateway rule deleted successfully' });
        fetchGatewayRules();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete gateway rule', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold">Sales Channel Management</h1>
            <p className="text-gray-500 text-sm md:text-base">Configure external sales channels and financial fee mappings</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Zap className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{channels.length}</div>
                <div className="text-sm text-gray-500">Total Channels</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Activity className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {channels.filter(c => c.status === 'active').length}
                </div>
                <div className="text-sm text-gray-500">Active Channels</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{gatewayRules.length}</div>
                <div className="text-sm text-gray-500">Fee Rules</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {gatewayRules.filter(r => r.is_active).length}
                </div>
                <div className="text-sm text-gray-500">Active Rules</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 grid w-full grid-cols-2">
            <TabsTrigger value="channels" className="flex items-center gap-2">
              <Store className="h-4 w-4" />
              <span className="hidden sm:inline">Sales Channels</span>
              <span className="sm:hidden">Channels</span>
            </TabsTrigger>
            <TabsTrigger value="fees" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <span className="hidden sm:inline">Financial Fee Mapping</span>
              <span className="sm:hidden">Fees</span>
            </TabsTrigger>
          </TabsList>

          {/* Sales Channels Tab */}
          <TabsContent value="channels">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Sales Channels</CardTitle>
                  <CardDescription>Configure external sales channels and integrations</CardDescription>
                </div>
                <Button onClick={() => {
                  setSelectedChannel(null);
                  setModalMode('add');
                  setShowChannelModal(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" /> Add Channel
                </Button>
              </CardHeader>
              <CardContent>
                {channels.length === 0 ? (
                  <div className="text-center py-12">
                    <Store className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">No sales channels configured</h3>
                    <p className="text-gray-500 mb-4">Add your first sales channel to start managing multi-channel sales</p>
                    <Button onClick={() => {
                      setSelectedChannel(null);
                      setModalMode('add');
                      setShowChannelModal(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" /> Add Channel
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {channels.map((channel) => (
                      <ChannelCard
                        key={channel.id}
                        channel={channel}
                        onEdit={(c) => {
                          setSelectedChannel(c);
                          setModalMode('edit');
                          setShowChannelModal(true);
                        }}
                        onDelete={handleDeleteChannel}
                        onManageKeys={(c) => {
                          setSelectedChannel(c);
                          setShowApiKeyModal(true);
                        }}
                        onManageWebhooks={(c) => {
                          toast({ title: 'Coming Soon', description: 'Webhook management will be available soon' });
                        }}
                        onManageFeeds={(c) => {
                          toast({ title: 'Coming Soon', description: 'Feed management will be available soon' });
                        }}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Financial Fee Mapping Tab */}
          <TabsContent value="fees">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Gateway Fee Rules</CardTitle>
                  <CardDescription>Configure payment gateway fees by market, channel, provider, and method</CardDescription>
                </div>
                <Button onClick={() => {
                  setSelectedRule(null);
                  setModalMode('add');
                  setShowGatewayModal(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" /> Add Rule
                </Button>
              </CardHeader>
              <CardContent>
                {gatewayRules.length === 0 ? (
                  <div className="text-center py-12">
                    <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">No gateway fee rules configured</h3>
                    <p className="text-gray-500 mb-4">Add fee rules to configure payment gateway charges</p>
                    <Button onClick={() => {
                      setSelectedRule(null);
                      setModalMode('add');
                      setShowGatewayModal(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" /> Add Rule
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="px-4 py-3 text-left text-sm font-medium">Rule Name</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Market</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Channel</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Provider</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Method</th>
                          <th className="px-4 py-3 text-right text-sm font-medium">Fee</th>
                          <th className="px-4 py-3 text-right text-sm font-medium">GST</th>
                          <th className="px-4 py-3 text-center text-sm font-medium">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gatewayRules.map((rule) => (
                          <GatewayFeeRuleRow
                            key={rule.id}
                            rule={rule}
                            onEdit={(r) => {
                              setSelectedRule(r);
                              setModalMode('edit');
                              setShowGatewayModal(true);
                            }}
                            onDelete={handleDeleteGatewayRule}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Fee Calculation Info */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-3">Payout Logic - Fee Deduction Flow</h4>
                  
                  <div className="space-y-4">
                    {/* Step 1 - Immediate */}
                    <div className="p-3 bg-orange-50 rounded border border-orange-200">
                      <h5 className="font-medium text-orange-900 mb-1">Step 1: IMMEDIATE Fees (PSP/Gateway)</h5>
                      <p className="text-sm text-orange-800">
                        Logged for audit. Already deducted by PSP from settlement.
                      </p>
                      <ul className="text-sm text-orange-700 mt-2 space-y-1 ml-4 list-disc">
                        <li><strong>Gateway Fee</strong> = (Gross × Rate%) + Fixed</li>
                        <li><strong>GST on Gateway</strong> = Gateway Fee × 18%</li>
                        <li><strong>PSP Settlement</strong> = Gross - Gateway Cost</li>
                      </ul>
                    </div>
                    
                    {/* Step 2 - Deferred */}
                    <div className="p-3 bg-purple-50 rounded border border-purple-200">
                      <h5 className="font-medium text-purple-900 mb-1">Step 2: DEFERRED Collection (Channel)</h5>
                      <p className="text-sm text-purple-800">
                        Collected from Vendor to cover future Shopify/Meta invoice.
                      </p>
                      <ul className="text-sm text-purple-700 mt-2 space-y-1 ml-4 list-disc">
                        <li><strong>Channel Liability</strong> = Gross × Collection Rate%</li>
                        <li><strong>Net After Channel</strong> = PSP Settlement - Channel Liability</li>
                      </ul>
                    </div>
                    
                    {/* Step 3 - Government Taxes */}
                    <div className="p-3 bg-red-50 rounded border border-red-200">
                      <h5 className="font-medium text-red-900 mb-1">Step 3: Government Taxes (TCS/TDS)</h5>
                      <div className="text-sm text-red-800 space-y-2">
                        <div className="p-2 bg-white/50 rounded">
                          <strong>GST TCS (1%)</strong> - On Net Taxable Value
                          <ul className="ml-4 list-disc text-red-700">
                            <li>Net Taxable Value = Gross - Returns/Discounts</li>
                            <li>TCS Amount = Net Taxable Value × 1%</li>
                          </ul>
                        </div>
                        <div className="p-2 bg-white/50 rounded">
                          <strong>Income Tax TDS (0.1% - Sec 194-O)</strong> - On Gross Sales
                          <ul className="ml-4 list-disc text-red-700">
                            <li>TDS Amount = Gross Sales × 0.1%</li>
                            <li className="font-medium">CONDITIONAL: Only for Individual/HUF with FY turnover &gt; ₹5 Lakhs</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    {/* Step 4 - Marketplace */}
                    <div className="p-3 bg-green-50 rounded border border-green-200">
                      <h5 className="font-medium text-green-900 mb-1">Step 4: Marketplace Commission & Payout</h5>
                      <ul className="text-sm text-green-700 mt-2 space-y-1 ml-4 list-disc">
                        <li><strong>Commission</strong> = (Net After Channel - Taxes) × Rate%</li>
                        <li><strong>Vendor Payout</strong> = Net After Taxes - Commission</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Modals */}
        <ChannelModal
          isOpen={showChannelModal}
          onClose={() => setShowChannelModal(false)}
          onSave={handleSaveChannel}
          channel={selectedChannel}
          mode={modalMode}
        />

        <GatewayFeeRuleModal
          isOpen={showGatewayModal}
          onClose={() => setShowGatewayModal(false)}
          onSave={handleSaveGatewayRule}
          rule={selectedRule}
          mode={modalMode}
        />

        <APIKeyModal
          isOpen={showApiKeyModal}
          onClose={() => setShowApiKeyModal(false)}
          channel={selectedChannel}
          token={token}
        />
      </div>
    </DashboardLayout>
  );
};

export default SalesChannelManagement;
